import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Transition, Dialog } from '@headlessui/react';
import dayjs from 'dayjs';

import { NotReadyToChatIcon, ReadyToChatIcon } from 'src/assets/icons';

import { Spinner } from 'src/ui/components';

import { useGetAlertRevisions } from '../api';

import { classNames } from 'src/utils/className';
import {
  getPtTimeZoneDate,
  getPtTimeIn24hr,
  getTimezoneFromAbbreviation,
} from 'src/utils/getPTZone';
import { calculateAgeAsString } from 'src/utils/common';
import * as utilityFn from 'src/utils/common';

import {
  PhoneType,
  AlertRevisonListType,
  AccountAlertResponse,
} from '../types/accountAlerts';
import { PersonAlertListType } from '../types/personAlerts';
import { CountryObj } from 'src/types/countryType';
import { DiffComparison } from '../utils/DiffComparison';

type AlertHistoryPropsType = {
  isAlertHistoryOpen: boolean;
  setIsAlertHistoryOpen: (
    value: boolean | ((prevState: boolean) => boolean),
  ) => void;
  alertRevisions: AlertRevisonListType[] | undefined;
  selectedAlert: PersonAlertListType | undefined;
  setSelectedAlert: any;
  countryList: Array<CountryObj> | undefined;
};

function htmlToPlainText(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  function extract(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    return Array.from(node.childNodes).map(extract).join(' ');
  }
  return extract(div).replace(/\s+/g, ' ').trim();
}

function annotateCurrentHtml(html: string, segments: DiffSegment[]): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  const currSegs = segments.filter(s => s.type !== 'removed');
  let segIdx = 0;

  function processTextNode(textNode: Text): void {
    const raw = textNode.textContent || '';
    const words = raw.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return;
    const leadWs = raw.match(/^\s+/)?.[0] ?? '';
    const trailWs = raw.match(/\s+$/)?.[0] ?? '';
    const frag = document.createDocumentFragment();
    if (leadWs) frag.appendChild(document.createTextNode(leadWs));
    words.forEach((word, i) => {
      if (i > 0) frag.appendChild(document.createTextNode(' '));
      const seg = currSegs[segIdx++];
      if (seg?.type === 'added') {
        const span = document.createElement('span');
        span.className =
          'rounded bg-green-100 px-1 py-0.5 text-green-800 dark:bg-green-900/30 dark:text-green-400';
        span.textContent = word;
        frag.appendChild(span);
      } else {
        frag.appendChild(document.createTextNode(word));
      }
    });
    if (trailWs) frag.appendChild(document.createTextNode(trailWs));
    textNode.parentNode?.replaceChild(frag, textNode);
  }

  function walk(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      processTextNode(node as Text);
    } else {
      Array.from(node.childNodes).forEach(walk);
    }
  }

  walk(div);
  return div.innerHTML;
}

type DiffSegment = { type: 'equal' | 'added' | 'removed'; text: string };

function reorderDiffSegments(segments: DiffSegment[]): DiffSegment[] {
  const result: DiffSegment[] = [];
  let i = 0;
  while (i < segments.length) {
    if (segments[i].type === 'equal') {
      result.push(segments[i]);
      i++;
    } else {
      const removed: DiffSegment[] = [];
      const added: DiffSegment[] = [];
      while (i < segments.length && segments[i].type !== 'equal') {
        if (segments[i].type === 'removed') removed.push(segments[i]);
        else added.push(segments[i]);
        i++;
      }
      result.push(...removed, ...added);
    }
  }
  return result;
}

function computeWordDiff(currText: string, prevText: string): DiffSegment[] {
  const currTokens = currText.trim().split(/\s+/).filter(Boolean);
  const prevTokens = prevText.trim().split(/\s+/).filter(Boolean);
  const m = currTokens.length;
  const n = prevTokens.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (currTokens[i - 1] === prevTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  const segments: DiffSegment[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && currTokens[i - 1] === prevTokens[j - 1]) {
      segments.unshift({ type: 'equal', text: currTokens[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      segments.unshift({ type: 'removed', text: prevTokens[j - 1] });
      j--;
    } else {
      segments.unshift({ type: 'added', text: currTokens[i - 1] });
      i--;
    }
  }
  return segments;
}

export function AlertHistory({
  isAlertHistoryOpen,
  setIsAlertHistoryOpen,
  alertRevisions,
  selectedAlert,
  setSelectedAlert,
  countryList,
}: AlertHistoryPropsType) {
  const [selectedRevision, setSelectedRevision] = useState<any>();
  const [selectedRevisionIndex, setSelectedRevisionIndex] = useState<number>(0);
  const [showDifferences, setShowDifferences] = useState<boolean>(false);
  const showDifferencesRef = useRef<boolean>(false);
  showDifferencesRef.current = showDifferences;
  const wasAutoUnchecked = useRef<boolean>(false);

  const { data: revisionDetails, isLoading: isRevisionDetailsLoading } =
    useGetAlertRevisions(
      selectedAlert?.Person?.AccountId,
      selectedAlert?.Id,
      selectedRevision?.Id,
    );

  const sortedRevisions = useMemo(() => {
    return alertRevisions?.sort(
      (a: any, b: any) =>
        new Date(b.UpdatedOn).getTime() - new Date(a.UpdatedOn).getTime(),
    );
  }, [alertRevisions]);

  const isFirst = useMemo(() => {
    if (!sortedRevisions || sortedRevisions.length === 0) return true;
    return selectedRevisionIndex >= sortedRevisions.length - 1;
  }, [sortedRevisions, selectedRevisionIndex]);

  const previousRevision = useMemo(() => {
    if (!sortedRevisions || selectedRevisionIndex === undefined)
      return undefined;
    return sortedRevisions[selectedRevisionIndex + 1];
  }, [sortedRevisions, selectedRevisionIndex]);

  const {
    data: previousRevisionDetails,
    isLoading: isPreviousRevisionLoading,
  } = useGetAlertRevisions(
    selectedAlert?.Person?.AccountId,
    selectedAlert?.Id,
    previousRevision?.Id,
  );

  useEffect(() => {
    if (alertRevisions) {
      setSelectedRevision(alertRevisions[0]);
      setSelectedRevisionIndex(0);
    }
  }, [alertRevisions]);

  useEffect(() => {
    if (isFirst) {
      wasAutoUnchecked.current = showDifferencesRef.current;
      setShowDifferences(false);
    } else if (wasAutoUnchecked.current) {
      setShowDifferences(true);
      wasAutoUnchecked.current = false;
    }
  }, [isFirst]);

  const countryCode = useMemo(() => {
    if (countryList) {
      return utilityFn.countryOptions(countryList);
    }
  }, [countryList]);

  function onModalClose() {
    setIsAlertHistoryOpen(false);
    setSelectedRevision(undefined);
    setSelectedAlert(undefined);
    setShowDifferences(false);
    setSelectedRevisionIndex(0);
  }

  function getCountryCode(countryId: number | undefined | null) {
    if (countryId)
      return countryList?.find((item: any) => item?.Id === countryId)?.ISDCode;
    else return '+1';
  }

  function getFormattedNumber(phoneInfo: PhoneType | undefined | null) {
    if (phoneInfo && phoneInfo?.Number) {
      const isUsFormat = utilityFn.hasUsFormat(
        countryCode,
        phoneInfo?.CountryId || 0,
      );
      const phonenumber = utilityFn.formatPhoneNumber(
        phoneInfo?.Number,
        isUsFormat,
      );
      return phonenumber;
    }
  }

  function computeResetInterval(
    revDetails: AccountAlertResponse | null | undefined,
  ) {
    if (!revDetails?.PersonCallLimitRevision) return '';
    const isoString = revDetails.PersonCallLimitRevision.ResetIntervalOffset;
    const timezone = revDetails.PersonCallLimitRevision.TimeZone?.Abbreviation;
    const timezoneAbb = getTimezoneFromAbbreviation(timezone || '');
    return dayjs(isoString).tz(timezoneAbb).format('hh:mm A');
  }

  function applyDiff(
    current?: string | number | null,
    previous?: string | number | null,
  ): React.ReactNode {
    if (!showDifferences || isFirst || !previousRevisionDetails) {
      return current !== undefined && current !== null ? current : null;
    }
    return DiffComparison(current, previous, isFirst);
  }

  function renderHtmlDiff(
    current?: string | null,
    previous?: string | null,
  ): React.ReactNode {
    if (!showDifferences || isFirst || !previousRevisionDetails) {
      return (
        <div
          className="prose max-w-full dark:prose-invert"
          dangerouslySetInnerHTML={utilityFn.createMarkup(current || '')}
        />
      );
    }

    const curr = (current || '').trim();
    const prev = (previous || '').trim();

    if (curr === prev) {
      return (
        <div
          className="prose max-w-full dark:prose-invert"
          dangerouslySetInnerHTML={utilityFn.createMarkup(current || '')}
        />
      );
    }

    const currText = htmlToPlainText(curr);
    const prevText = htmlToPlainText(prev);
    const allSegments = reorderDiffSegments(
      computeWordDiff(currText, prevText),
    );
    const removedSegments = allSegments.filter(s => s.type === 'removed');
    const annotatedHtml = annotateCurrentHtml(curr, allSegments);

    return (
      <div className="space-y-1">
        {removedSegments.length > 0 && (
          <p className="text-sm">
            {removedSegments.map((seg, idx) => {
              const isLast = idx === removedSegments.length - 1;
              return (
                <span
                  key={idx}
                  className="rounded bg-red-100 px-1 py-0.5 text-red-800 line-through dark:bg-red-900/30 dark:text-red-400"
                >
                  {isLast ? seg.text : `${seg.text} `}
                </span>
              );
            })}
          </p>
        )}
        <div
          className="prose max-w-full dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: annotatedHtml }}
        />
      </div>
    );
  }

  function renderRevisionList(item: AlertRevisonListType, index: number) {
    return (
      <div
        key={item?.Id}
        className={classNames(
          'cursor-pointer rounded-md border px-2 py-1 !leading-none',
          selectedRevision?.Id === item.Id
            ? 'border-primary-400 bg-primary-100  dark:border-primary-800/50 dark:bg-primary-900' // active styles
            : 'border-gray-200 bg-white dark:border-neutral-700/50 dark:bg-neutral-700/60', // default styles
        )}
        onClick={() => {
          setSelectedRevision(item);
          setSelectedRevisionIndex(index);
        }}
      >
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {`${getPtTimeZoneDate(item.UpdatedOn)}  ${getPtTimeIn24hr(
            item.UpdatedOn,
          )}`}
        </p>
        <p className="text-xs text-gray-600 dark:text-white/50">
          {`Edited by: ${item.UpdatedBy}`}
        </p>
        {item.UpdatedFromAlertId !== item.PersonAlertId ? (
          <p className="text-xs text-gray-600 dark:text-white/50">
            {`Updated due to change in Alert ID: `}
            <span className="font-bold">{item.UpdatedFromAlertId}</span>
          </p>
        ) : null}
      </div>
    );
  }

  function renderAccountSection() {
    return (
      <dl className="border dark:border-dark-medium">
        <h3 className="sticky -top-px border-b bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
          Account
        </h3>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Account name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.AccountNameAndNumber}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">CID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.CID}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">SID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.SID}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">PID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.PID}
          </dd>
        </div>
      </dl>
    );
  }

  function renderPhoneNumber(
    phoneInfo: PhoneType | undefined | null,
    label: string,
    previousPhoneInfo?: PhoneType | undefined | null,
  ) {
    const showPhoneDiff =
      showDifferences && !isFirst && !!previousRevisionDetails;

    return (
      <div className="flex gap-2 border-b dark:border-dark-medium">
        <dt className="w-1/3 p-2 text-sm font-medium">{label}</dt>
        <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
          {showPhoneDiff ? (
            <div className="space-y-0.5">
              {(phoneInfo?.PhoneType?.Description ||
                previousPhoneInfo?.PhoneType?.Description) && (
                <p>
                  {DiffComparison(
                    phoneInfo?.PhoneType?.Description,
                    previousPhoneInfo?.PhoneType?.Description,
                    isFirst,
                  )}
                </p>
              )}
              <p>
                {DiffComparison(
                  [
                    getCountryCode(phoneInfo?.CountryId),
                    getFormattedNumber(phoneInfo),
                  ]
                    .filter(Boolean)
                    .join(' ') || null,
                  [
                    getCountryCode(previousPhoneInfo?.CountryId),
                    getFormattedNumber(previousPhoneInfo),
                  ]
                    .filter(Boolean)
                    .join(' ') || null,
                  isFirst,
                )}
              </p>
              {(phoneInfo?.OkToLeaveMessage !== null &&
                phoneInfo?.OkToLeaveMessage !== undefined) ||
              (previousPhoneInfo?.OkToLeaveMessage !== null &&
                previousPhoneInfo?.OkToLeaveMessage !== undefined) ? (
                <p>
                  {DiffComparison(
                    phoneInfo?.OkToLeaveMessage !== null &&
                      phoneInfo?.OkToLeaveMessage !== undefined
                      ? phoneInfo.OkToLeaveMessage
                        ? 'OK to leave message'
                        : 'Not OK to leave message'
                      : null,
                    previousPhoneInfo?.OkToLeaveMessage !== null &&
                      previousPhoneInfo?.OkToLeaveMessage !== undefined
                      ? previousPhoneInfo.OkToLeaveMessage
                        ? 'OK to leave message'
                        : 'Not OK to leave message'
                      : null,
                    isFirst,
                  )}
                </p>
              ) : null}
              {(phoneInfo?.Comments || previousPhoneInfo?.Comments) && (
                <p>
                  <label className="text-sm font-medium">Comments: </label>
                  {DiffComparison(
                    phoneInfo?.Comments,
                    previousPhoneInfo?.Comments,
                    isFirst,
                  )}
                </p>
              )}
            </div>
          ) : (
            <>
              <p>
                {phoneInfo?.PhoneType?.Description && (
                  <label className="text-sm font-medium">
                    {phoneInfo.PhoneType.Description}:
                  </label>
                )}
                {getCountryCode(phoneInfo?.CountryId)} {''}{' '}
                {getFormattedNumber(phoneInfo)}
                {phoneInfo?.OkToLeaveMessage === true ? (
                  <ReadyToChatIcon className="ms-1 text-green-700 dark:text-green-400" />
                ) : (
                  phoneInfo?.OkToLeaveMessage === false && (
                    <NotReadyToChatIcon className="ms-1 text-red-700 dark:text-red-400" />
                  )
                )}
              </p>
              {phoneInfo?.Comments && phoneInfo?.Comments?.length > 0 && (
                <p>
                  <label className="text-sm font-medium">Comments: </label>
                  {phoneInfo?.Comments}
                </p>
              )}
            </>
          )}
        </dd>
      </div>
    );
  }

  function renderAlertInfo() {
    const prevRevision = previousRevisionDetails;
    return (
      <dl className="border dark:border-dark-medium">
        <h3 className="sticky -top-px border-b bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
          Alert information
        </h3>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Counselor or Case manager (if applicable)
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(
              revisionDetails?.CounselorOrCaseManager,
              prevRevision?.CounselorOrCaseManager,
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Special program participant
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(
              revisionDetails?.SpecialProgramsParticipant,
              prevRevision?.SpecialProgramsParticipant,
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Situation description
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {renderHtmlDiff(
              revisionDetails?.SituationDescription,
              prevRevision?.SituationDescription,
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Telephone intervention description
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {renderHtmlDiff(
              revisionDetails?.TelephoneInterventionDescription,
              prevRevision?.TelephoneInterventionDescription,
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Contact limit</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            <p>
              <label className="text-sm font-medium">
                Is contact limit set?:{' '}
              </label>
              {applyDiff(
                revisionDetails?.PersonCallLimitRevision === null
                  ? 'No'
                  : 'Yes',
                prevRevision?.PersonCallLimitRevision === null ? 'No' : 'Yes',
              )}
            </p>
            {revisionDetails &&
              revisionDetails?.PersonCallLimitRevision !== null && (
                <>
                  <p>
                    <label className="text-sm font-medium">Calls: </label>
                    {applyDiff(
                      revisionDetails?.PersonCallLimitRevision?.MaxCallCount,
                      prevRevision?.PersonCallLimitRevision?.MaxCallCount,
                    )}
                  </p>
                  <p>
                    <label className="text-sm font-medium">Reset time: </label>
                    {applyDiff(
                      computeResetInterval(revisionDetails),
                      computeResetInterval(prevRevision),
                    )}
                  </p>
                  <p>
                    <label className="text-sm font-medium">Timezone: </label>
                    {applyDiff(
                      revisionDetails?.PersonCallLimitRevision?.TimeZone
                        ?.Abbreviation,
                      prevRevision?.PersonCallLimitRevision?.TimeZone
                        ?.Abbreviation,
                    )}
                  </p>
                </>
              )}
          </dd>
        </div>
      </dl>
    );
  }

  function renderPersonInfo() {
    const prevRevision = previousRevisionDetails;
    const demographics = revisionDetails?.PersonDemographicsRevision;
    const prevDemographics = prevRevision?.PersonDemographicsRevision;

    return (
      <dl className="border dark:border-dark-medium">
        <h3 className="sticky -top-px border-b bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
          Person Information
        </h3>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Alert ID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonAlertId}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Entered by</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.UpdatedBy}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Entered date</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.UpdatedOn !== null
              ? dayjs(revisionDetails?.UpdatedOn).format('MM/DD/YYYY H:mm:ss')
              : ''}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Start date - End date
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(
              revisionDetails?.StartDate != null
                ? dayjs(revisionDetails.StartDate).format('MM/DD/YYYY HH:mm:ss')
                : '',
              prevRevision?.StartDate != null
                ? dayjs(prevRevision.StartDate).format('MM/DD/YYYY HH:mm:ss')
                : '',
            )}{' '}
            -{' '}
            {applyDiff(
              revisionDetails?.EndDate != null
                ? dayjs(revisionDetails.EndDate).format('MM/DD/YYYY HH:mm:ss')
                : '',
              prevRevision?.EndDate != null
                ? dayjs(prevRevision.EndDate).format('MM/DD/YYYY HH:mm:ss')
                : '',
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Reason for Alert</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(revisionDetails?.Reasons, prevRevision?.Reasons)}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            {`Person alert limiter${
              revisionDetails?.LimitCategory
                ? ` - [${revisionDetails.LimitCategory}]`
                : ''
            }`}
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(
              revisionDetails?.PersonAlertLimiterValues,
              prevRevision?.PersonAlertLimiterValues,
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Person ID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedRevision?.PersonId}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">First name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(demographics?.FirstName, prevDemographics?.FirstName)}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Last name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(demographics?.LastName, prevDemographics?.LastName)}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Alternate name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {applyDiff(
              demographics?.PreferredName,
              prevDemographics?.PreferredName,
            )}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Date of Birth</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {(demographics?.Birthdate !== null ||
              prevDemographics?.Birthdate !== null) && (
              <p>
                <label className="text-sm font-medium">Birth date: </label>
                {applyDiff(
                  demographics?.Birthdate
                    ? dayjs(demographics.Birthdate).format('MM/DD/YYYY')
                    : null,
                  prevDemographics?.Birthdate
                    ? dayjs(prevDemographics.Birthdate).format('MM/DD/YYYY')
                    : null,
                )}
              </p>
            )}
            {(demographics?.Age !== null && demographics?.Age !== '') ||
            (prevDemographics?.Age !== null && prevDemographics?.Age !== '') ? (
              <div>
                <label className="text-sm font-medium">Age: </label>
                {applyDiff(
                  demographics?.Age !== null && demographics?.Age !== ''
                    ? demographics?.Age
                    : demographics?.Birthdate
                    ? calculateAgeAsString(demographics.Birthdate)
                    : null,
                  prevDemographics?.Age !== null && prevDemographics?.Age !== ''
                    ? prevDemographics?.Age
                    : prevDemographics?.Birthdate
                    ? calculateAgeAsString(prevDemographics.Birthdate)
                    : null,
                )}
              </div>
            ) : null}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Address</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {demographics?.PrimaryAddress !== null ? (
              <>
                <p>
                  <span className="font-semibold">Street 1: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.Street1,
                    prevDemographics?.PrimaryAddress?.Street1,
                  )}
                </p>
                <p>
                  <span className="font-semibold">Street 2: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.Street2,
                    prevDemographics?.PrimaryAddress?.Street2,
                  )}
                </p>
                <p>
                  {' '}
                  <span className="font-semibold">Street 3: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.Street3,
                    prevDemographics?.PrimaryAddress?.Street3,
                  )}
                </p>
                <p>
                  {' '}
                  <span className="font-semibold">City: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.City,
                    prevDemographics?.PrimaryAddress?.City,
                  )}
                </p>
                <p>
                  {' '}
                  <span className="font-semibold">State/Province: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.StateProvince?.Name,
                    prevDemographics?.PrimaryAddress?.StateProvince?.Name,
                  )}
                </p>
                <p>
                  <span className="font-semibold">Zip/Postal code: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.ZipPostalCode,
                    prevDemographics?.PrimaryAddress?.ZipPostalCode,
                  )}
                </p>
                <p>
                  <span className="font-semibold">Country: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.Country?.Name,
                    prevDemographics?.PrimaryAddress?.Country?.Name,
                  )}
                </p>
                <p>
                  <span className="font-semibold">County: </span>
                  {applyDiff(
                    demographics?.PrimaryAddress?.County,
                    prevDemographics?.PrimaryAddress?.County,
                  )}
                </p>
              </>
            ) : null}
          </dd>
        </div>
        {renderPhoneNumber(
          demographics?.PrimaryPhone,
          'Primary phone',
          prevDemographics?.PrimaryPhone,
        )}

        {renderPhoneNumber(
          demographics?.SecondaryPhone,
          'Secondary phone',
          prevDemographics?.SecondaryPhone,
        )}
      </dl>
    );
  }

  return (
    <Transition appear show={isAlertHistoryOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          onModalClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pr-dialog__backdrop" />
        </Transition.Child>

        <div className="pr-dialog__wrapper">
          <div className="pr-dialog__panel__container sm:!grid-rows-[1fr_auto_1fr]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                style={{ maxHeight: 'calc(100svh - 90px)' }}
                className="pr-dialog__panel relative flex flex-col sm:!max-w-5xl"
              >
                <div className="flex items-center gap-3 border-b p-4 dark:border-dark-medium">
                  <Dialog.Title
                    as="h3"
                    className="grow truncate text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Alert history
                  </Dialog.Title>

                  <label
                    className={classNames(
                      'flex items-center gap-2 whitespace-nowrap text-sm',
                      isFirst
                        ? 'cursor-not-allowed text-gray-400 dark:text-gray-600'
                        : 'cursor-pointer text-gray-700 dark:text-gray-300',
                    )}
                    title={
                      isFirst
                        ? 'No previous revision to compare'
                        : 'Show field-level changes from the previous revision'
                    }
                  >
                    <input
                      type="checkbox"
                      checked={showDifferences}
                      onChange={e => setShowDifferences(e.target.checked)}
                      disabled={isFirst}
                      className="h-4 w-4 rounded border-gray-400 text-primary checked:dark:bg-primary-500 focus:ring-light-focus disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-600 dark:text-primary-400 dark:focus:ring-dark-focus"
                    />
                    Show changes
                  </label>

                  <button
                    type="button"
                    className=" text-gray-400 hover:text-gray-500 sm:right-4 sm:top-4"
                    onClick={() => {
                      onModalClose();
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="relative min-h-0 grow">
                  <div className="grid h-full grid-cols-[minmax(160px,25%)_1fr]">
                    <div className="space-y-2 overflow-y-scroll scroll-smooth bg-gray-100 p-2 dark:bg-neutral-800">
                      {sortedRevisions &&
                        sortedRevisions?.map((item: any, index: number) =>
                          renderRevisionList(item, index),
                        )}
                    </div>
                    <div className="relative my-4 overflow-y-scroll scroll-smooth">
                      <div className="min-w-80 space-y-4 px-4">
                        {renderAccountSection()}
                        {renderPersonInfo()}
                        {renderAlertInfo()}
                      </div>
                      {(isRevisionDetailsLoading ||
                        isPreviousRevisionLoading) && (
                        <div className="absolute inset-0 z-20 grid place-items-center bg-white/60 dark:bg-black/70">
                          <Spinner size="large" className="z-10" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
